import React from "react";
import styled from "styled-components";
import { ColorV2 } from "config/colors";
import { TypographyV2 } from "config/font";

type HeadingLevel = "h1" | "h2" | "h3" | "h4" | "h5";

interface HeadingProps {
  level: HeadingLevel;
  children: React.ReactNode;
  color?: ColorV2;
  margin?: string;
}

const BaseHeading = styled.h1<{ $color: ColorV2; $margin: string }>`
  margin: ${(props) => props.$margin};
  color: ${(props) => props.$color};
`;

const HeadingH1 = styled(BaseHeading)`
  ${TypographyV2.h1}
`;

const HeadingH2 = styled(BaseHeading)`
  ${TypographyV2.h2}
`;

const HeadingH3 = styled(BaseHeading)`
  ${TypographyV2.h3}
`;

const HeadingH4 = styled(BaseHeading)`
  ${TypographyV2.h4}
`;

const HeadingH5 = styled(BaseHeading)`
  ${TypographyV2.h5}
`;

interface BaseHeadingProps {
  $color: ColorV2;
  $margin: string;
  as: HeadingLevel;
  children: React.ReactNode;
}

const COMPONENT_BY_LEVEL: Record<
  HeadingLevel,
  React.ComponentType<BaseHeadingProps>
> = {
  h1: HeadingH1,
  h2: HeadingH2,
  h3: HeadingH3,
  h4: HeadingH4,
  h5: HeadingH5,
};

export const Heading = ({
  level,
  children,
  color = ColorV2.Blue1,
  margin = "0",
}: HeadingProps): React.ReactElement => {
  const Component = COMPONENT_BY_LEVEL[level];

  return (
    <Component as={level} $color={color} $margin={margin}>
      {children}
    </Component>
  );
};
